import { useMutation } from '@apollo/client';
import React from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

// Hasura runs these mutation fields in order. resetDefault is
// relationship-guarded so stale row ids do not clear the prompt's current
// server-side default.
const setDefaultPromptTemplateModelMutation = graphql(`
  mutation SetDefaultPromptTemplateModelMutation(
    $promptTemplateId: uuid!
    $promptTemplateModelId: uuid!
  ) {
    resetDefault: updateLlmModelPromptTemplates(
      where: {
        _and: [
          { promptTemplateId: { _eq: $promptTemplateId } }
          {
            template: {
              prompt_models: {
                id: { _eq: $promptTemplateModelId }
                promptTemplateId: { _eq: $promptTemplateId }
              }
            }
          }
        ]
      }
      _set: { isDefault: false }
    ) {
      affectedRows
    }
    setDefault: updateLlmModelPromptTemplates(
      where: {
        _and: [
          { id: { _eq: $promptTemplateModelId } }
          { promptTemplateId: { _eq: $promptTemplateId } }
        ]
      }
      _set: { isDefault: true }
    ) {
      affectedRows
      returning {
        __typename
        id
        isDefault
      }
    }
  }
`);

const promptModelDefaultsFragment = graphql(`
  fragment PromptModelDefaults on PromptTemplates {
    __typename
    id
    prompt_models {
      __typename
      id
      modelArgsId
      isDefault
    }
  }
`);

export interface SetDefaultPromptTemplateModelActionProps {
  isDefault: boolean;
  promptTemplateId: string;
  promptTemplateModelId: string;
}

export const SetDefaultPromptTemplateModelAction: React.FC<
  SetDefaultPromptTemplateModelActionProps
> = ({ promptTemplateModelId, promptTemplateId, isDefault }) => {
  const toast = useToast();

  const [setDefaultPromptTemplateModel] = useMutation(
    setDefaultPromptTemplateModelMutation,
    {
      onError: () =>
        toast.error('Failed to update default model for this prompt'),
      onCompleted: (data) => {
        if (data.setDefault?.affectedRows === 1) {
          return;
        }

        toast.error('Failed to update default model for this prompt');
      },
      optimisticResponse: {
        resetDefault: {
          affectedRows: 1,
        },
        setDefault: {
          affectedRows: 1,
          returning: [
            {
              __typename: 'LlmModelPromptTemplates' as const,
              id: promptTemplateModelId,
              isDefault: true,
            },
          ],
        },
      },
      update: (_cache, result) => {
        if (result.data?.setDefault?.affectedRows !== 1) {
          return;
        }

        cacheUtils.updateFragment(
          {
            fragment: promptModelDefaultsFragment,
            key: {
              id: promptTemplateId,
            },
          },
          (existing) => {
            return {
              __typename: 'PromptTemplates' as const,
              id: promptTemplateId,
              prompt_models: (existing?.prompt_models ?? []).map((pm) => ({
                __typename: 'LlmModelPromptTemplates' as const,
                id: pm.id,
                isDefault: pm.id === promptTemplateModelId,
                modelArgsId: pm.modelArgsId,
              })),
            };
          },
        );
      },
    },
  );

  const setDefault = () => {
    setDefaultPromptTemplateModel({
      variables: {
        promptTemplateId,
        promptTemplateModelId,
      },
    });
  };

  if (isDefault) {
    return null;
  }

  return (
    <AlertDialog>
      <SimpleTooltip label="Make this model the default when this prompt is used">
        <AlertDialogTrigger asChild>
          <Button variant="outline">
            <Icon name="Gem" size="xl" />
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will make this model configuration the default any time a user
            uses this prompt
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={setDefault}>
            Set Default
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
